import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";

// Hero Carousel — mobile 1:1 + desktop 16:9 with independent state, dots, and swipe.
// Preloads images for instant swaps.
export default function HeroCarousel() {
  const [mobile, setMobile] = useState([]);
  const [desktop, setDesktop] = useState([]);

  useEffect(() => {
    api.get("/media?kind=hero_mobile").then((r) => setMobile(r.data.map((m) => m.url))).catch(() => {});
    api.get("/media?kind=hero_desktop").then((r) => setDesktop(r.data.map((m) => m.url))).catch(() => {});
  }, []);

  // Preload all images once URLs are known so swipes are instant.
  useEffect(() => {
    [...mobile, ...desktop].forEach((url) => {
      if (!url) return;
      const img = new Image();
      img.src = url;
    });
  }, [mobile, desktop]);

  return (
    <section data-testid="hero-carousel" className="w-full bg-ivory">
      <div className="block md:hidden">
        <SingleCarousel slides={mobile.length ? mobile : [null, null, null]} aspectClass="aspect-1-1" variant="mobile" />
      </div>
      <div className="hidden md:block">
        <SingleCarousel slides={desktop.length ? desktop : [null, null]} aspectClass="aspect-16-9" variant="desktop" />
      </div>
    </section>
  );
}

function SingleCarousel({ slides, aspectClass, variant }) {
  const [idx, setIdx] = useState(0);
  const count = slides.length;
  const timerRef = useRef(null);
  const touchStart = useRef(0);

  const go = (i) => setIdx(((i % count) + count) % count);

  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setIdx((cur) => (cur + 1) % count), 3000);
    return () => clearTimeout(timerRef.current);
  }, [idx, count]);

  const onTouchStart = (e) => (touchStart.current = e.touches[0].clientX);
  const onTouchEnd = (e) => {
    const dx = e.changedTouches[0].clientX - touchStart.current;
    if (Math.abs(dx) > 45) go(dx < 0 ? idx + 1 : idx - 1);
  };

  return (
    <div className={`relative w-full overflow-hidden ${aspectClass}`} onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
      <div
        className="flex h-full w-full transition-transform duration-700 ease-[cubic-bezier(0.22,1,0.36,1)] will-change-transform"
        style={{ transform: `translateX(-${idx * 100}%)` }}
      >
        {slides.map((src, i) => (
          <div key={i} data-testid={`hero-slide-${variant}-${i}`} className="w-full h-full flex-shrink-0 bg-ivory-soft">
            {src ? (
              <img
                src={src}
                alt={`Slide ${i + 1}`}
                className="w-full h-full object-cover block"
                draggable={false}
                loading={i === 0 ? "eager" : "eager"}
                fetchPriority={i === 0 ? "high" : "auto"}
                decoding="async"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-brown-light text-sm tracking-widest uppercase">
                Slide {i + 1}
              </div>
            )}
          </div>
        ))}
      </div>
      <div className="absolute bottom-4 md:bottom-6 left-0 right-0 flex justify-center gap-2 z-10">
        {slides.map((_, i) => (
          <button
            key={i}
            data-testid={`hero-dot-${variant}-${i}`}
            aria-label={`Slide ${i + 1}`}
            onClick={() => go(i)}
            className={`h-2 rounded-full transition-all duration-300 ${i === idx ? "bg-brand-red w-6" : "bg-white/80 border border-brown-border w-2"}`}
          />
        ))}
      </div>
    </div>
  );
}
